import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Bell,
  Calendar,
  Tag,
  AlertCircle,
  CheckCircle2,
  Clock,
  Building,
} from "lucide-react";
import { DataTable } from "../../../components/Common/DataTable";
import ReleaseNotificationModal from "./ReleaseNotificationModal";
import { apiClient } from "../../../config/api";
import API_CONFIG from "../../../config/api";

const NOTIFICATION_TYPES = {
  feature: { label: "Feature", color: "info" },
  maintenance: { label: "Maintenance", color: "warning" },
  security: { label: "Security", color: "destructive" },
  info: { label: "Information", color: "secondary" },
  update: { label: "Update", color: "success" },
};

const PRIORITY_LEVELS = {
  low: { label: "Low", color: "secondary" },
  medium: { label: "Medium", color: "info" },
  high: { label: "High", color: "warning" },
  critical: { label: "Critical", color: "destructive" },
};

const STATUS_OPTIONS = {
  draft: { label: "Draft", color: "secondary", icon: Edit2 },
  scheduled: { label: "Scheduled", color: "warning", icon: Clock },
  published: { label: "Published", color: "success", icon: CheckCircle2 },
  expired: { label: "Expired", color: "destructive", icon: AlertCircle },
};

export default function ReleaseNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: "add", // 'add' | 'edit' | 'delete'
    notification: null,
  });

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        status: filters.status || '',
        type: filters.type || '',
        clientId: filters.clientId || '',
        page: filters.page || 1,
        pageSize: filters.pageSize || 50
      });

      const response = await apiClient.get(
        `${API_CONFIG.ENDPOINTS.RELEASE_NOTIFICATIONS}?${params}`
      );

      // Handle both paginated and non-paginated responses
      const data = response.data;
      setNotifications(data.items || data || []);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
      setError(err.response?.data?.error || "Failed to load notifications");
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleAddNotification = () => {
    setModalState({
      isOpen: true,
      mode: "add",
      notification: null,
    });
  };

  const handleEditNotification = (notification) => {
    setModalState({
      isOpen: true,
      mode: "edit",
      notification,
    });
  };

  const handleDeleteNotification = (notification) => {
    setModalState({
      isOpen: true,
      mode: "delete",
      notification,
    });
  };

  const handleCloseModal = () => {
    setModalState({
      isOpen: false,
      mode: "add",
      notification: null,
    });
  };

  const handleSaveNotification = async (notificationData) => {
    try {
      if (modalState.mode === "add") {
        // Create notification via API
        await apiClient.post(
          API_CONFIG.ENDPOINTS.RELEASE_NOTIFICATIONS,
          notificationData
        );
      } else if (modalState.mode === "edit") {
        // Update notification via API
        await apiClient.put(
          API_CONFIG.ENDPOINTS.RELEASE_NOTIFICATION(modalState.notification.id),
          notificationData
        );
      }
      // Refresh the list after save
      await fetchNotifications();
      handleCloseModal();
    } catch (err) {
      console.error("Failed to save notification:", err);
      throw err; // Re-throw so modal can handle the error
    }
  };

  const handleConfirmDelete = async () => {
    try {
      // Delete notification via API
      await apiClient.delete(
        API_CONFIG.ENDPOINTS.RELEASE_NOTIFICATION(modalState.notification.id)
      );
      // Refresh the list after delete
      await fetchNotifications();
      handleCloseModal();
    } catch (err) {
      console.error("Failed to delete notification:", err);
      throw err; // Re-throw so modal can handle the error
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Stats calculation
  const stats = useMemo(() => {
    const total = notifications.length;
    const published = notifications.filter((n) => n.status === "published").length;
    const scheduled = notifications.filter((n) => n.status === "scheduled").length;
    const drafts = notifications.filter((n) => n.status === "draft").length;
    return { total, published, scheduled, drafts };
  }, [notifications]);

  // Table columns definition
  const columns = useMemo(
    () => [
      {
        id: "title",
        header: "Notification",
        accessorKey: "title",
        cell: ({ row }) => (
          <div className="notification-cell">
            <div className="notification-cell__icon">
              <Bell size={16} />
            </div>
            <div className="notification-cell__info">
              <span className="notification-cell__title">{row.original.title}</span>
              <span className="notification-cell__message">
                {row.original.message.length > 60
                  ? `${row.original.message.substring(0, 60)}...`
                  : row.original.message}
              </span>
            </div>
          </div>
        ),
        size: 280,
      },
      {
        id: "type",
        header: "Type",
        accessorKey: "type",
        cell: ({ row }) => {
          const type = NOTIFICATION_TYPES[row.original.type] || NOTIFICATION_TYPES.info;
          return (
            <span className={`chip chip--${type.color}`}>
              <Tag size={12} />
              {type.label}
            </span>
          );
        },
        size: 120,
      },
      {
        id: "priority",
        header: "Priority",
        accessorKey: "priority",
        cell: ({ row }) => {
          const priority = PRIORITY_LEVELS[row.original.priority] || PRIORITY_LEVELS.medium;
          return (
            <span className={`chip chip--${priority.color}`}>
              {priority.label}
            </span>
          );
        },
        size: 100,
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => {
          const status = STATUS_OPTIONS[row.original.status] || STATUS_OPTIONS.draft;
          const StatusIcon = status.icon;
          return (
            <span className={`chip chip--${status.color}`}>
              <StatusIcon size={12} />
              {status.label}
            </span>
          );
        },
        size: 120,
      },
      {
        id: "targetClient",
        header: "Target Client",
        accessorKey: "targetClientName",
        cell: ({ row }) => (
          <div className="client-cell">
            <Building size={14} className="icon-muted" />
            <span className={row.original.targetClientId === "all" ? "client-cell--all" : ""}>
              {row.original.targetClientName || "All Clients"}
            </span>
          </div>
        ),
        size: 140,
      },
      {
        id: "publishedAt",
        header: "Published",
        accessorKey: "publishedAt",
        cell: ({ row }) => (
          <div className="date-cell">
            <Calendar size={14} className="icon-muted" />
            <span>{formatDate(row.original.publishedAt)}</span>
          </div>
        ),
        size: 160,
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="action-buttons">
            <button
              className="btn btn--icon btn--outline"
              onClick={(e) => {
                e.stopPropagation();
                handleEditNotification(row.original);
              }}
              title="Edit notification"
            >
              <Edit2 size={16} />
            </button>
            <button
              className="btn btn--icon btn--danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteNotification(row.original);
              }}
              title="Delete notification"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ),
        size: 100,
        enableSorting: false,
      },
    ],
    []
  );

  return (
    <div className="release-notifications">
      {/* Header */}
      <div className="release-notifications__header">
        <div>
          <h2 className="release-notifications__title">Release Notifications</h2>
          <p className="release-notifications__subtitle">
            Manage system-wide notifications and announcements for users
          </p>
        </div>
        <div className="release-notifications__actions">
          <button className="btn btn--primary" onClick={handleAddNotification}>
            <Plus size={16} />
            New Notification
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="release-notifications__stats">
        <div className="stat-item">
          <span className="stat-item__label">Total Notifications</span>
          <span className="stat-item__value">{stats.total}</span>
        </div>
        <div className="stat-item">
          <span className="stat-item__label">Published</span>
          <span className="stat-item__value stat-item__value--success">{stats.published}</span>
        </div>
        <div className="stat-item">
          <span className="stat-item__label">Scheduled</span>
          <span className="stat-item__value stat-item__value--warning">{stats.scheduled}</span>
        </div>
        <div className="stat-item">
          <span className="stat-item__label">Drafts</span>
          <span className="stat-item__value stat-item__value--muted">{stats.drafts}</span>
        </div>
      </div>

      {/* DataTable */}
      <DataTable
        data={notifications}
        columns={columns}
        loading={loading}
        enableSearch={true}
        enableFilters={true}
        enableColumnVisibility={true}
        enableSorting={true}
        enablePagination={true}
        enableExport={true}
        enableDensity={true}
        pageSize={10}
        emptyMessage="No release notifications found. Create your first notification!"
      />

      {/* Modal */}
      {modalState.isOpen && (
        <ReleaseNotificationModal
          mode={modalState.mode}
          notification={modalState.notification}
          onClose={handleCloseModal}
          onSave={handleSaveNotification}
          onDelete={handleConfirmDelete}
        />
      )}
    </div>
  );
}

